import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, Send, Linkedin } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        message: formData.subject ? `[${formData.subject}] ${formData.message}` : formData.message,
      });
      toast.success('Your message has been sent to our sales team at sales@iiypv.com. We\'ll get back to you soon.');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
      if (process.env.NODE_ENV !== 'production') {
        console.error('Contact form error:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <span className="text-sm font-medium tracking-widest uppercase text-primary mb-4 block">
            Get In Touch
          </span>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 font-heading" data-testid="contact-title">
            Contact Us
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Have a question or need assistance? We're here to help!
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-3xl p-8 shadow-soft text-center"
            data-testid="contact-card-email"
          >
            <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Mail className="w-8 h-8 text-primary" />
            </div>
            <h3 className="text-xl font-bold mb-2 font-heading">Email Us</h3>
            <p className="text-muted-foreground mb-4">Sales &amp; Support &mdash; reply within 1 business day</p>
            <div className="space-y-1.5">
              <a href="mailto:sales@iiypv.com" className="block text-primary hover:underline font-medium" data-testid="contact-email-link">
                sales@iiypv.com
              </a>
              <a href="mailto:support@iiypv.com" className="block text-primary hover:underline font-medium" data-testid="contact-support-email-link">
                support@iiypv.com
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-3xl p-8 shadow-soft text-center"
            data-testid="contact-card-phone"
          >
            <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Phone className="w-8 h-8 text-primary" />
            </div>
            <h3 className="text-xl font-bold mb-2 font-heading">Call Us</h3>
            <p className="text-muted-foreground mb-4">Mon-Fri 9am-6pm GMT</p>
            <a href="tel:+447442554379" className="text-primary hover:underline font-medium" data-testid="contact-phone-number">
              +44 (0)74425 54379
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-3xl p-8 shadow-soft text-center"
            data-testid="contact-card-linkedin"
          >
            <div className="w-16 h-16 bg-[#0077B5]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Linkedin className="w-8 h-8 text-[#0077B5]" />
            </div>
            <h3 className="text-xl font-bold mb-2 font-heading">Connect on LinkedIn</h3>
            <p className="text-muted-foreground mb-4">Follow us for latest updates</p>
            <a 
              href="https://www.linkedin.com/company/110393231/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#0077B5] hover:underline font-medium"
              data-testid="contact-linkedin-link"
            >
              IIY LinkedIn
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-3xl p-8 md:p-12 shadow-soft max-w-3xl mx-auto"
        >
          <h2 className="text-3xl font-bold mb-2 font-heading text-center" data-testid="contact-form-title">Send Us a Message</h2>
          <p className="text-center text-muted-foreground mb-8">Your enquiry will be sent directly to <strong>sales@iiypv.com</strong></p>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-4 rounded-xl border-2 border-border focus:border-primary focus:outline-none transition-colors"
                  placeholder="John Doe"
                  data-testid="contact-name-input"
                  required
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-4 rounded-xl border-2 border-border focus:border-primary focus:outline-none transition-colors"
                  placeholder="john@example.com"
                  data-testid="contact-email-input"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="subject" className="block text-sm font-medium mb-2">
                Subject
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className="w-full px-4 py-4 rounded-xl border-2 border-border focus:border-primary focus:outline-none transition-colors"
                placeholder="How can we help?"
                data-testid="contact-subject-input"
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium mb-2">
                Message *
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={6}
                className="w-full px-4 py-4 rounded-xl border-2 border-border focus:border-primary focus:outline-none transition-colors resize-none"
                placeholder="Tell us about your enquiry..."
                data-testid="contact-message-input"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 hover:-translate-y-1 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl disabled:opacity-50"
              data-testid="contact-submit-button"
            >
              <Send className="w-5 h-5" />
              {loading ? 'Sending...' : 'Send Message'}
            </button>
          </form>
          <p className="text-xs text-muted-foreground/70 text-center mt-6">
            This website and contact channel is operated by <strong>IIY Phytoventra Ltd</strong>. IIY [Invest In Yourself]&reg; is a Registered Trademark.
          </p>
        </motion.div>
      </div>
    </div>
  );
}